"use client"

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { Check, ChevronDown, ChevronRight, ListChecks, X } from 'lucide-react'
import { useStore } from '@/lib/store'
import { cn } from '@/lib/utils'
import { firstDaySteps, showFirstDay } from '@/lib/first-day'
import { useT } from '@/lib/i18n'

/**
 * Step 3a of the owner's plan (2026-09-26): the first day, one thin line above
 * every page until the new shop can really sell — „პირველი დღე · 3/6 ·
 * შემდეგი: …". Opens to the whole list, each step a link to where it is done.
 * Every step is detected from the shop's own data (lib/first-day.ts), so
 * nothing is ever ticked by hand and nothing lies.
 *
 * The owner's alone; hidden until the store has loaded (an unloaded store
 * looks exactly like an empty shop), for a shop past its first sales, and
 * once dismissed — `settings.firstDayDone`, so it stays gone on every device.
 */
export function FirstDayBanner() {
  const t = useT()
  const isOwner = useStore((s) => s.userRole === 'owner' && !s.supportSession)
  const hydrated = useStore((s) => s.isHydrated)
  const historyReady = useStore((s) => s.historyReady)
  const settings = useStore((s) => s.settings)
  const categories = useStore((s) => s.categories.length)
  const products = useStore((s) => s.products.length)
  const cashiers = useStore((s) => s.cashiers.length)
  const pinIsDefault = useStore((s) => s.pinIsDefault)
  const sales = useStore((s) => s.sales.filter((x) => x.type === 'sale').length)
  const weighed = useStore((s) => s.products.filter((p) => p.unit === 'kg').length)
  const withExpiry = useStore((s) => s.products.filter((p) => p.track_expiry || !!p.expiry_date).length)   // 1.61: „ვადიანი“
  const combos = useStore((s) => s.productAddons.length)
  const models = useStore((s) => new Set(s.products.map((p) => p.model_id).filter(Boolean)).size)
  const customers = useStore((s) => s.customers.length)
  const services = useStore((s) => s.products.filter((p) => !!p.is_service).length)
  const supplierCodes = useStore((s) => s.supplierCodes.length)
  const measured = useStore((s) => s.products.filter((p) => p.unit === 'm' || p.unit === 'kg' || p.unit === 'l').length)
  const updateSettings = useStore((s) => s.updateSettings)
  const [open, setOpen] = useState(false)

  const input = {
    companyName: settings.companyName, companyId: settings.companyId, categories, products, cashiers, pinIsDefault, sales,
    shopType: settings.shopType, weighed, combos, withExpiry, models, customers, services, supplierCodes, measured,
  }
  const steps = useMemo(() => firstDaySteps(input), // eslint-disable-line react-hooks/exhaustive-deps
    // t: the steps' words follow the interface language
    [settings.companyName, settings.companyId, categories, products, cashiers, pinIsDefault, sales, settings.shopType, weighed, combos, withExpiry, models, customers, services, supplierCodes, measured, t])

  if (!isOwner || !hydrated || !historyReady || !showFirstDay(input, settings.firstDayDone)) return null
  // 1.55 — a suggestion is shown and ticked, but the count, the bar and „next" are the required steps'
  const required = steps.filter((s) => !s.optional)
  const done = required.filter((s) => s.done).length
  const next = required.find((s) => !s.done)!

  return (
    <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] text-sm">
      <div className="flex items-center gap-2.5 px-3.5 py-2.5">
        <span className="size-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <ListChecks className="size-4" />
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 min-w-0 text-left flex items-center gap-2">
          <span className="font-black text-foreground shrink-0">{t('fd.banner.1')}</span>
          <span className="font-bold tabular-nums text-primary shrink-0">{done}/{required.length}</span>
          <span className="text-muted-foreground truncate hidden sm:inline">{t('fd.banner.2')}{next.label}</span>
          <ChevronDown className={cn('size-4 text-muted-foreground shrink-0 transition-transform', open && 'rotate-180')} />
        </button>
        {/* A phone has no room for a label beside „პირველი დღე 2/6" (it covered
            the count): the arrow alone there, the words from sm up. */}
        <Link href={next.href} aria-label={next.label}
          className="h-8 px-2 sm:px-3 rounded-lg bg-primary text-primary-foreground text-[13px] font-bold flex items-center gap-1 shrink-0 hover:bg-primary/90">
          <span className="hidden sm:inline">{next.label.length > 18 ? t('fd.banner.3') : next.label}</span> <ChevronRight className="size-4 sm:size-3.5" />
        </Link>
        <button type="button" onClick={() => updateSettings({ firstDayDone: true })} aria-label={t('fd.banner.4')}
          className="size-8 rounded-lg text-muted-foreground hover:bg-muted/60 flex items-center justify-center shrink-0">
          <X className="size-4" />
        </button>
      </div>
      <div className="h-1 mx-3.5 mb-2 rounded-full bg-primary/10 overflow-hidden">
        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(done / required.length) * 100}%` }} />
      </div>

      {open && (
        <ol className="px-3.5 pb-3 flex flex-col gap-1">
          {steps.map((s) => (
            <li key={s.key}>
              <Link href={s.href} onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-card/70 dark:hover:bg-white/5">
                <span className={cn('size-6 rounded-full flex items-center justify-center shrink-0 border-2',
                  s.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-border')}>
                  {s.done && <Check className="size-3.5" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block font-bold', s.done ? 'text-muted-foreground line-through' : 'text-foreground')}>
                    {s.label}
                    {s.optional && <span className="ml-1.5 align-middle text-[12px] font-bold text-primary bg-primary/10 rounded-md px-1.5 py-0.5">{t('fd.optional')}</span>}
                  </span>
                  {!s.done && <span className="block text-[12px] text-muted-foreground">{s.hint}</span>}
                </span>
                {!s.done && <ChevronRight className="size-4 text-muted-foreground shrink-0" />}
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
